import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import LoadingSkeletonModalTable from '@/components/modal/prompt/LoadingSkeletonModalTable';
import HaggleParticipantsTable from '@/components/table/haggle-event/HaggleParticipantsTable';
import HaggleSalesReportTable from '@/components/table/haggle-event/HaggleSalesReportTable';
import { calculateStatus, formatDateTime } from '@/components/utils/utils';
import { ApiQueryKeys } from '@/interfaces/api/ApiQueryKeys';
import {
	IHaggleEvent,
	IHaggleEventMinimalProduct,
} from '@/interfaces/api/haggle-event/IHaggleEvent';
import { IHaggleEventSalesReport } from '@/interfaces/api/haggle-event/IHaggleEventSalesReport';
import { haggleApiService } from '@/services/haggle.service';
import { notificationService } from '@/services/notification.service';

const HaggleSingleEventDashboard = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const [activeTab, setActiveTab] = useState<'sales' | 'participants'>('sales');

	const {
		data: haggleEvents,
		isLoading: haggleEventIsLoading,
		isError: haggleEventIsError,
		error: haggleEventError,
	} = useQuery<IHaggleEventSalesReport>({
		queryKey: [ApiQueryKeys.getHaggleEventSalesReport, id],
		queryFn: () => haggleApiService.getHaggleEventSalesReport(Number(id)),
		refetchOnWindowFocus: false,
	});

	const { data: haggleEvent, isLoading: isFetching } = useQuery<IHaggleEvent>({
		queryKey: [ApiQueryKeys.getHaggleEventById, id],
		queryFn: () => haggleApiService.getHaggleEventById(Number(id)),
		refetchOnWindowFocus: false,
	});

	if (haggleEventIsError) {
		if (haggleEventError instanceof Error) {
			notificationService.error(haggleEventError.message);
		} else {
			notificationService.error('Unknown error fetching haggle events');
		}
		navigate('/user/dashboard/haggle-events');
	}

	return (
		<div className="bg-grey-5 min-h-content overflow-y-auto ">
			{haggleEventIsLoading && isFetching && <LoadingSkeletonModalTable />}

			{haggleEvents && !haggleEventIsLoading && !isFetching && (
				<div className="flex flex-col h-full grow gap-4">
					{/* Haggle Details */}
					<div className="bg-white rounded-md shadow p-6 mb-4">
						<h2 className="text-xl font-semibold mb-4">Haggle Event Details</h2>

						<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
							{/* Basic Info */}
							<div className="space-y-4">
								<div>
									<h3 className="text-sm font-medium text-gray-500">
										Haggle ID
									</h3>
									<p className="mt-1 text-base">{haggleEvents.id}</p>
								</div>

								<div>
									<h3 className="text-sm font-medium text-gray-500">Status</h3>
									<p className="mt-1">
										<span
											className={`px-2 py-1 text-xs font-medium rounded-full ${
												calculateStatus(
													haggleEvents.endDate,
													haggleEvents.startDate,
												) === 'ACTIVE'
													? 'bg-purpleBrand-100 text-purpleBrand-800'
													: calculateStatus(
															haggleEvents.endDate,
															haggleEvents.startDate,
													  ) === 'PENDING'
													? 'bg-yellow-100 text-yellow-800'
													: 'bg-gray-100 text-gray-800'
											}`}
										>
											{calculateStatus(
												haggleEvents.endDate,
												haggleEvents.startDate,
											)}
										</span>
									</p>
								</div>
							</div>

							{/* Dates */}
							<div className="space-y-4">
								<div>
									<h3 className="text-sm font-medium text-gray-500">
										Start Date
									</h3>
									<p className="mt-1 text-base">
										{formatDateTime(haggleEvents.startDate)}
									</p>
								</div>

								<div>
									<h3 className="text-sm font-medium text-gray-500">
										End Date
									</h3>
									<p className="mt-1 text-base">
										{formatDateTime(haggleEvents.endDate)}
									</p>
								</div>
							</div>

							{/* Financial */}
							<div className="space-y-4">
								<div>
									<h3 className="text-sm font-medium text-gray-500">
										Max. Discount
									</h3>
									<p className="mt-1 text-base">
										{haggleEvents.acceptableMargin}%
									</p>
								</div>

								<div>
									<h3 className="text-sm font-medium text-gray-500">
										Completed Deals
									</h3>
									<p className="mt-1 text-base">
										{
											haggleEvents.agreements.filter(
												(agreement) => agreement.isSold,
											).length
										}
										/ {haggleEvents.agreements.length}
									</p>
								</div>
							</div>
						</div>

						<div className="mb-4 p-3 bg-blue-50 rounded-md mt-4">
							<h3 className="font-semibold">Sales Summary</h3>
							<p>
								Total sales from completed transactions:{' '}
								<span className="font-semibold">
									${haggleEvents.totalSalesMade}
								</span>
							</p>
							<p>
								Total profit from completed transactions:{' '}
								<span className="font-semibold">
									${haggleEvents.totalProfit}
								</span>
							</p>
						</div>

						{/* Products */}
						<div className="mt-8">
							<h3 className="text-sm font-medium mb-3">
								Products in this Haggle
							</h3>
							<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
								{haggleEvents.productIds.map((productId) => {
									const product = haggleEvent?.minimalProducts?.find(
										(p: IHaggleEventMinimalProduct) =>
											p.id.toString() === productId,
									);
									return product ? (
										<div
											key={productId}
											className="flex items-center p-3 border rounded-md"
										>
											<div className="h-[30px] w-[30px] mr-3 bg-gray-100 rounded-md overflow-hidden">
												{product.image ? (
													<img
														src={product.image}
														alt={product.title}
														className="h-full w-full object-contain"
													/>
												) : (
													<div className="h-full w-full flex items-center justify-center text-gray-400">
														<svg
															xmlns="http://www.w3.org/2000/svg"
															className="h-5 w-5"
															viewBox="0 0 20 20"
															fill="currentColor"
														>
															<path
																fillRule="evenodd"
																d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4V5h12v10z"
																clipRule="evenodd"
															/>
														</svg>
													</div>
												)}
											</div>
											<div className="flex-1">
												<p className="font-medium text-sm">{product.title}</p>
											</div>
										</div>
									) : null;
								})}
							</div>
						</div>
					</div>

					{/* Tabs */}
					<div className="bg-white rounded-md shadow">
						<div className="flex justify-start gap-4 p-4 border-b">
							<button
								className={`px-4 py-2 rounded ${
									activeTab === 'sales'
										? 'bg-purpleBrand-500 hover:bg-purpleBrand-600 text-white'
										: 'bg-gray-200 hover:bg-gray-300'
								}`}
								onClick={() => setActiveTab('sales')}
							>
								Sales Report
							</button>
							<button
								className={`px-4 py-2 rounded ${
									activeTab === 'participants'
										? 'bg-purpleBrand-500 hover:bg-purpleBrand-600 text-white'
										: 'bg-gray-200 hover:bg-gray-300'
								}`}
								onClick={() => setActiveTab('participants')}
							>
								Participants ({haggleEvents.participantEmails?.length || 0})
							</button>
						</div>

						<div className="p-4">
							{activeTab === 'sales' && (
								<HaggleSalesReportTable
									haggle={haggleEvents}
									products={haggleEvent?.minimalProducts || []}
								/>
							)}

							{activeTab === 'participants' && (
								<HaggleParticipantsTable
									participantEmails={haggleEvents.participantEmails || []}
								/>
							)}
						</div>
					</div>
				</div>
			)}
		</div>
	);
};
export default HaggleSingleEventDashboard;
